import { Loader2, Send } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface QueryInputProps {
    onSendMessage: (message: string) => void;
    isLoading: boolean;
    disabled?: boolean;
    registerPrefill?: (fn: (value: string) => void) => void;
}

export const QueryInput: React.FC<QueryInputProps> = ({
    onSendMessage,
    isLoading,
    disabled = false,
    registerPrefill,
}) => {
    const [query, setQuery] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const prefill = useCallback((value: string) => {
        setQuery(value);
        textareaRef.current?.focus();
    }, []);

    useEffect(() => {
        registerPrefill?.(prefill);
    }, [registerPrefill, prefill]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (query.trim() && !isLoading && !disabled) {
            onSendMessage(query.trim());
            setQuery('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [query]);

    return (
        <div className="query-input-wrapper">
            <form onSubmit={handleSubmit} className="query-input-form">
                <div className="query-input-container">
                    <textarea
                        ref={textareaRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about specific problems, e.g., 'Any F-16 issues?' or 'Bad actors in Avionics'"
                        className="query-textarea"
                        rows={1}
                        disabled={isLoading || disabled}
                        maxLength={1000}
                    />
                    <button
                        type="submit"
                        className="send-button"
                        disabled={!query.trim() || isLoading || disabled}
                        title="Send message"
                    >
                        {isLoading ? (
                            <Loader2 size={20} className="spinning" />
                        ) : (
                            <Send size={16} />
                        )}
                    </button>
                </div>
                <div className="query-input-footer">
                    <span className="char-count">
                        {query.length}/1000
                    </span>
                    <span className="input-hint">
                        Press Enter to send, Shift+Enter for new line
                    </span>
                </div>
            </form>
        </div>
    );
};
